import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  FolderOpen, 
  Calendar, 
  Users, 
  GraduationCap,
  MoreVertical,
  Edit,
  Trash2,
  Eye,
  Lock,
  BookOpen
} from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { API_BASE } from '../config/api';
import { mediaUrl } from '../lib/mediaUrl';
import { useAuth } from '../contexts/AuthContext';

const API = `${API_BASE}/api`;

const ProgrammeCover = ({ url, name }) => (
  <div className="relative aspect-video bg-gradient-to-br from-zinc-800 via-zinc-900 to-teal-950/40 overflow-hidden">
    {url ? <img src={mediaUrl(url)} alt={name || 'Programme'} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" data-testid="programme-card-thumbnail" />
      : <GraduationCap className="absolute inset-0 m-auto h-10 w-10 text-zinc-700" />}
    <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-zinc-900 to-transparent" />
  </div>
);

// Programme Card Component - defined outside to avoid re-renders
const ProgrammeCard = ({ programme, isAdmin, navigate, handleDelete, enrolment }) => (
  <Card className="bg-zinc-900 border-zinc-800 hover:border-zinc-700 transition-colors overflow-hidden group" data-testid={`programme-card-${programme.programme_id}`}>
    <ProgrammeCover url={programme.thumbnail_url} name={programme.programme_name} />
    <CardHeader className="pb-3">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <Badge 
              variant={programme.is_published ? "default" : "secondary"}
              className={programme.is_published ? "bg-teal-500/20 text-teal-400" : "bg-zinc-700 text-zinc-400"}
            >
              {programme.is_published ? "Published" : "Draft"}
            </Badge>
            <Badge variant="outline" className="border-zinc-700 text-zinc-400">
              {programme.structure_type === 'weeks' ? 'Weekly' : 'Monthly'}
            </Badge>
          </div>
          <CardTitle className="text-lg text-white line-clamp-2">
            {programme.programme_name || 'Untitled Programme'}
          </CardTitle>
        </div>
        
      </div>
    </CardHeader>
    
    <CardContent className="space-y-4">
      {programme.description && (
        <p className="text-sm text-zinc-400 line-clamp-2">{programme.description}</p>
      )}
      
      <div className="grid grid-cols-2 gap-3 text-sm">
        {programme.professional_body && (
          <div className="flex items-center gap-2 text-zinc-400">
            <GraduationCap className="h-4 w-4 text-teal-500" />
            <span className="truncate">{programme.professional_body}</span>
          </div>
        )}
        {programme.duration && (
          <div className="flex items-center gap-2 text-zinc-400">
            <Calendar className="h-4 w-4 text-amber-500" />
            <span>{programme.duration}</span>
          </div>
        )}
        {programme.nqf_level && (
          <div className="flex items-center gap-2 text-zinc-400">
            <FolderOpen className="h-4 w-4 text-purple-500" />
            <span>NQF {programme.nqf_level}</span>
          </div>
        )}
        <div className="flex items-center gap-2 text-zinc-400">
          <Users className="h-4 w-4 text-blue-500" />
          <span>{programme.enrolled_count || 0} enrolled</span>
        </div>
      </div>
      
      <div className="pt-3 border-t border-zinc-800 flex gap-2">
        {isAdmin || enrolment ? (
          <Button 
            variant="outline" 
            size="sm" 
            className="flex-1 border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            onClick={() => navigate(`/programmes/${programme.programme_id}`)}
            data-testid={`programme-view-${programme.programme_id}`}
          >
            <Eye className="h-4 w-4 mr-2" />
            {enrolment && !isAdmin ? `Continue · ${enrolment.progress_pct || 0}% complete` : 'View'}
          </Button>
        ) : (
          <div className="flex-1 flex items-center justify-center gap-2 h-9 rounded-md border border-zinc-800 bg-zinc-900/60 text-xs text-zinc-500" data-testid={`programme-not-enrolled-${programme.programme_id}`}>
            <Lock className="h-3.5 w-3.5" />Not enrolled · contact your administrator
          </div>
        )}
        {isAdmin && (
          <Button 
            size="sm" 
            className="flex-1 bg-teal-600 hover:bg-teal-700 text-white"
            onClick={() => navigate('/admin?tab=programmes')}
            data-testid={`manage-in-admin-${programme.programme_id}`}
          >
            <Edit className="h-4 w-4 mr-2" />
            Manage in Admin
          </Button>
        )}
      </div>
    </CardContent>
  </Card>
);

export default function ProgrammesPage() {
  const navigate = useNavigate();
  const { token, isAuthenticated, user } = useAuth();
  const [programmes, setProgrammes] = useState([]);
  const [loading, setLoading] = useState(true);

  const headers = { Authorization: `Bearer ${token}` };
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  const [myEnrolments, setMyEnrolments] = useState({});

  useEffect(() => {
    fetchProgrammes();
  }, [isAdmin]);

  const fetchProgrammes = async () => {
    try {
      // Admin sees all programmes, others see only published
      const endpoint = isAdmin ? `${API}/programmes/admin/all` : `${API}/programmes`;
      const [res, mine] = await Promise.all([
        axios.get(endpoint, { headers }),
        isAdmin ? Promise.resolve({ data: { programmes: [] } }) : axios.get(`${API}/learner/programmes`, { headers }).catch(() => ({ data: { programmes: [] } })),
      ]);
      setProgrammes(res.data.programmes || []);
      setMyEnrolments(Object.fromEntries((mine.data.programmes || []).map((p) => [p.programme_id, p])));
    } catch (error) {
      console.error('Failed to fetch programmes:', error);
      // Don't show error for public endpoint if empty
      if (isAdmin) {
        toast.error('Failed to load programmes');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (programmeId) => {
    if (!window.confirm('Are you sure you want to delete this programme? This will remove all associated data.')) {
      return;
    }

    try {
      await axios.delete(`${API}/programmes/${programmeId}`, { headers });
      toast.success('Programme deleted');
      fetchProgrammes();
    } catch (error) {
      toast.error('Failed to delete programme');
    }
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-4xl font-bold text-white mb-2">
              Professional Programmes
            </h1>
            <p className="text-zinc-400">
              Structured learning pathways with multiple content formats
            </p>
          </div>
          
        </div>

        {/* Programmes Grid */}
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 animate-pulse">
                <div className="h-4 bg-zinc-800 rounded w-1/4 mb-4" />
                <div className="h-6 bg-zinc-800 rounded w-3/4 mb-4" />
                <div className="h-4 bg-zinc-800 rounded w-full mb-2" />
                <div className="h-4 bg-zinc-800 rounded w-2/3" />
              </div>
            ))}
          </div>
        ) : programmes.length === 0 ? (
          <div className="text-center py-16">
            <FolderOpen className="h-16 w-16 text-zinc-600 mx-auto mb-4" />
            <h3 className="text-xl font-medium text-white mb-2">No programmes yet</h3>
            <p className="text-zinc-400 mb-6" data-testid="programmes-empty-text">
              {isAdmin 
                ? "Programmes are created in the Admin panel (Admin → Programmes)."
                : "No programmes are available at the moment"}
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {programmes.map((programme) => (
              <ProgrammeCard 
                key={programme.programme_id} 
                programme={programme} 
                isAdmin={isAdmin}
                navigate={navigate}
                handleDelete={handleDelete}
                enrolment={myEnrolments[programme.programme_id]}
              />
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
